import { GameEvent, User } from '@/types'
import { request } from '@/utils/requests/request'
import { MessageAction, OutcomingMessage } from '../main-frame/message-types'

interface PurchaseOpts {
    amount: number
    description?: string
    photoUrl?: string
    externalId?: string
}

interface PurchaseStatus {
    status: 'PAID' | 'FAILED'
    id: string
    amount: number
    externalId?: string
}

interface ChargeOpts {
    amount: number
}

export interface GameAttributes {
    clid?: string
    widgetId?: string
    userAcquisitionParams: Record<string, string>
}

const processPurchase = async (gameHru: string, purchaseOpts: PurchaseOpts, attributes: GameAttributes) => {
    const res = await request<PurchaseStatus>('/api/v1/in-game-payments/purchase', {
        method: 'POST',
        body: JSON.stringify({ order: purchaseOpts, gameHru, attributes }),
    })

    if (res.errors.length) {
        return {
            type: 'in-game-payments',
            action: MessageAction.purchaseStatus,
            originalMessageEvent: event as GameEvent,
            payload: {
                status: 'FAILED',
                error: res.errors.join(', '),
            },
        } as OutcomingMessage
    }

    return res.data
}

export type ChargeUserFunction = (amount: number) => Promise<User | null>

export const createInGamePayments = (gameHru: string, chargeUser: ChargeUserFunction, attributes: GameAttributes) => {
    const messageHandler = async (event: GameEvent) => {
        switch (event.data.action) {
            case MessageAction.requestPurchase: {
                const purchaseData = await processPurchase(
                    gameHru,
                    event.data.payload as unknown as PurchaseOpts,
                    attributes,
                )

                return {
                    type: 'in-game-payments',
                    action: MessageAction.purchaseStatus,
                    originalMessageEvent: event as GameEvent,
                    payload: purchaseData,
                } as OutcomingMessage
            }
            case MessageAction.requestTopup: {
                const userInfo = await chargeUser((event.data.payload as unknown as ChargeOpts)?.amount || 0)

                return {
                    type: 'in-game-payments',
                    action: MessageAction.topupStatus,
                    originalMessageEvent: event as GameEvent,
                    payload: userInfo,
                } as OutcomingMessage
            }
            default:
                return {
                    action: MessageAction.unknownMessageType,
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                } as OutcomingMessage
        }
    }

    return messageHandler
}

export type InGamePaymentsMessageHandler = ReturnType<typeof createInGamePayments>
